import i18n from "@/lib/i18n";
import { playerErrorMessage } from "@/lib/playerErrors";

/**
 * Lightweight browser "fingerprint" payload sent alongside every public play
 * submission. Combined server-side into a SHA-256 hash used to block replay
 * attempts on the same game — even across cleared cookies, fresh emails, or
 * a new IP address assigned by the ISP the next day.
 *
 * Primary stable signal = `device_id` (UUID v4 persisted in `localStorage`).
 * Secondary signals (timezone, screen, platform, language) are kept as a
 * fallback hash in case localStorage is cleared: the combo still has enough
 * entropy to block a casual rejouer on the same device.
 *
 * We intentionally do NOT fingerprint Canvas / Audio / WebGL / fonts: the
 * chosen set keeps the bundle tiny and stays friendly to shared-family-WiFi
 * false positives.
 */
const DEVICE_ID_KEY = "qrise_device_id";

function _randomUuid() {
  // Prefer the native crypto.randomUUID when available (all modern browsers).
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (e) {
    /* ignore and fall through */
  }
  // RFC4122-ish fallback using crypto.getRandomValues when available.
  try {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      // Set version (4) and variant bits as per RFC4122
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
      return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
        .slice(6, 8)
        .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
    }
  } catch (e) {
    /* ignore */
  }
  // Last-resort: Math.random (still fine for anti-rejeu dedup).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Returns (and lazily creates) a per-browser persistent UUID, stored in
 * `localStorage`. Returns `""` when storage is unavailable (e.g. strict
 * privacy mode) — the backend will then fall back to the soft fingerprint
 * signals only.
 */
export function getOrCreateDeviceId() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = _randomUuid();
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return "";
  }
}

export function getClientFingerprint() {
  try {
    const tz =
      (typeof Intl !== "undefined" &&
        Intl.DateTimeFormat &&
        Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      "";
    const lang =
      (typeof navigator !== "undefined" &&
        (navigator.language || (navigator.languages && navigator.languages[0]))) ||
      "";
    const screenStr =
      typeof window !== "undefined" && window.screen
        ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth || 0}`
        : "";
    // navigator.userAgentData.platform on Chromium; fall back to navigator.platform.
    let platform = "";
    if (typeof navigator !== "undefined") {
      platform =
        (navigator.userAgentData && navigator.userAgentData.platform) ||
        navigator.platform ||
        "";
    }
    return {
      device_id: getOrCreateDeviceId(),
      language: lang,
      timezone: tz,
      screen: screenStr,
      platform,
    };
  } catch (e) {
    return { device_id: "", language: "", timezone: "", screen: "", platform: "" };
  }
}

/**
 * Detect whether an axios/fetch error corresponds to the server's
 * "already participated on this game" block. Returns true when the backend
 * replied with HTTP 409 and `detail.code === "already_participated"`.
 */
export function isAlreadyParticipatedError(err) {
  const resp = err?.response;
  if (!resp || resp.status !== 409) return false;
  const d = resp.data?.detail;
  if (d && typeof d === "object" && (d.code === "already_participated" || d.code === "already_played_today")) return true;
  if (typeof d === "string" && d.toLowerCase().includes("déjà participé")) return true;
  return false;
}

/** Message of the 409 "already participated" response, in the game language. */
export function alreadyParticipatedMessage(err) {
  return playerErrorMessage(err, i18n.t("errors.already_participated"));
}

/**
 * Pull a user-readable message out of an axios error response.
 *  - Handles receipt-related server rejections (`receipt_already_used`,
 *    `receipt_too_old`, etc.) with their localised message.
 *  - Falls back to the stringified detail, then to a generic default.
 */
export function extractErrorMessage(err) {
  return playerErrorMessage(err);
}

